import { memo, useCallback, useEffect, useRef, useState } from 'react';

import useFetchData from '@/hooks/useFetchData';
import type { ApiResponse } from '@/services';
import { fetchViralTemplatePage } from '@/services/viralTemplate';
import { ViralTemplateExposeFromEnum } from '@/utils/eventlog/eventNewConfig';

import { useViralTemplateFormModal } from '../../../hooks/useViralTemplateFormModal';
import type { TplTemplateVO } from '../../template-ui/types';
import TemplateGrid from './TemplateGrid';

/** 大类「二级全量列表」：仅网格 + 分页 + 详情弹窗；小类 tab 由 URL / entry 决定，本页不再展示 tab UI */
interface TemplateListPageProps {
  /** 列表接口 `category`：如 `/ugc/1`…`/ugc/4` 或 `/ads`；热门不传 */
  listCategory?: string;
}

const PAGE_SIZE = 40;
const MAX_AUTO_FILL_PAGES = 2;

/** 快速 tab 切换时丢弃过期的列表请求结果，避免闪动旧数据 */
const staleListResponse = (
  pageNum: number,
  pageSize: number,
): ApiResponse<TplTemplateVO> => ({
  code: 200,
  success: true,
  hasMore: false,
  items: [],
  totalCount: 0,
  page: pageNum,
  pageSize,
});

const TemplateListPage = memo(function TemplateListPage({
  listCategory,
}: TemplateListPageProps) {
  const { openFormModal, loadingDetail } = useViralTemplateFormModal(
    ViralTemplateExposeFromEnum.TEMPLATE_SUB,
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoFillLoadingRef = useRef(false);
  const autoFillTimesRef = useRef(0);
  const requestSeqRef = useRef(0);

  const fetchFn = useCallback(
    async (pageNum: number, pageSize: number) => {
      const requestSeq = requestSeqRef.current;
      await Promise.resolve();
      if (requestSeq !== requestSeqRef.current) {
        return staleListResponse(pageNum, pageSize);
      }

      const res = await fetchViralTemplatePage({
        category: listCategory,
        pageNum,
        pageSize,
      });

      if (requestSeq !== requestSeqRef.current) {
        return staleListResponse(pageNum, pageSize);
      }

      return res;
    },
    [listCategory],
  );

  const { data, loading, hasMore, refresh, loadMore } = useFetchData<TplTemplateVO>({
    fetchFn,
    pageSize: PAGE_SIZE,
  });

  useEffect(() => {
    requestSeqRef.current += 1;
    autoFillTimesRef.current = 0;
    void refresh();
  }, [listCategory, refresh]);

  const handleLoadMore = useCallback(async () => {
    if (!hasMore) return;
    if (isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      await loadMore();
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, loadMore]);

  const onScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (autoFillLoadingRef.current) return;
    if (!hasMore) return;
    const threshold = 80;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
      void handleLoadMore();
    }
  }, [handleLoadMore, hasMore]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (autoFillLoadingRef.current) return;
    if (!hasMore) return;
    if (loading) return;
    if (autoFillTimesRef.current >= MAX_AUTO_FILL_PAGES) return;

    const needFill = el.scrollHeight <= el.clientHeight + 10;
    if (!needFill) return;

    autoFillLoadingRef.current = true;
    autoFillTimesRef.current += 1;
    setIsLoadingMore(true);
    loadMore()
      .finally(() => {
        autoFillLoadingRef.current = false;
        setIsLoadingMore(false);
      })
      .catch(() => {
        /* ignore */
      });
  }, [data?.length, hasMore, loadMore, loading]);

  return (
    <div className="flex flex-1 min-h-0 w-full flex-col">
      <TemplateGrid
        items={data || []}
        loading={loading}
        from={ViralTemplateExposeFromEnum.TEMPLATE_SUB}
        isLoadingMore={isLoadingMore}
        onReplicate={(tpl) => void openFormModal(tpl)}
        scrollContainerRef={containerRef}
        onScroll={onScroll}
      />

      {loadingDetail && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/25">
          <div className="h-[20px] w-[20px] animate-spin rounded-full border-2 border-[#4931FA]/40 border-t-[#4931FA]" />
        </div>
      )}
    </div>
  );
});

TemplateListPage.displayName = 'TemplateListPage';

export default TemplateListPage;
